// src/pages/Login.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { API_URL, TENANT_ID } from "../../config";
import { getMessage } from "../../data/locales";
import { clearSessionKeys } from "../../utils/sessionKeys";
import {
  loadRegistrationFormDraft,
  saveRegistrationFormDraft,
} from "../../utils/registrationFormDraft";
import {
  backupCheckoutCartCourses,
  mergeRegistrationCoursesWithCart,
  readCartCourses,
  restoreCheckoutCartBackup,
} from "../../utils/cartCourseSelection";
import { clearCurrentUserProfileCache } from "../../utils/profileCache";
import { applyOwnedCoursesToStoredCart } from "../../utils/applyOwnedCoursesToStoredCart";
import {
  persistLoginSession,
  resolvePostLoginPath,
} from "../../utils/sessionAuth";
import { markSessionAcknowledged, isSessionAcknowledged } from "../../utils/sessionGate";
import { revokeUnacknowledgedServerSession } from "../../utils/browserSessionGuard";
import "./Login.css";  

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [mostrarModal, setMostrarModal] = useState(false);
  const staleSessionCleanupRef = useRef(Promise.resolve());

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("reason") === "inactivity") {
      setNotice(getMessage("login.inactivityNotice"));
      params.delete("reason");
      const nextSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isSessionAcknowledged()) {
      return;
    }

    staleSessionCleanupRef.current = revokeUnacknowledgedServerSession({ preserveCart: true }).catch(() => {
      /* stale cookie cleanup must not block the login form */
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!TENANT_ID || String(TENANT_ID).trim() === "") {
      setError("Configuración del tenant no disponible. Contacta con soporte.");
      return;
    }
    
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) { setError("Email inválido"); return; }
    if ((password || "").length < 6) { setError("La contraseña debe tener al menos 6 caracteres"); return; }
    
    setSubmitting(true);
    
    try {
      await staleSessionCleanupRef.current;
      const tenantId = (TENANT_ID ?? "").trim();
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Id": tenantId,
        },
        credentials: "include",
        body: JSON.stringify({ email, contraseña: password }),
      });

      if (response.status === 429) {
        const retry = response.headers.get("Retry-After");
        const seconds = retry ? parseInt(retry, 10) : NaN;
        let msg = "Demasiados intentos. Intenta de nuevo más tarde.";
        if (!Number.isNaN(seconds)) {
          const mins = Math.ceil(seconds / 60);
          msg = `Demasiados intentos. Vuelve a intentar en ${mins} min.`;
        }
        setError(msg);
        setSubmitting(false);
        return;
      }

      const data = await response.json();

      if (response.ok) {
        const cartCourses = readCartCourses();
        backupCheckoutCartCourses(cartCourses);
        clearSessionKeys({ preserveCart: true });
        clearCurrentUserProfileCache();
        markSessionAcknowledged();

        const restoredCart = cartCourses.length > 0 ? cartCourses : restoreCheckoutCartBackup();
        if (restoredCart.length > 0) {
          localStorage.setItem("carrito", JSON.stringify(restoredCart));
          const cartCourseIds = restoredCart
            .map((item) => (item && typeof item._id === "string" ? item._id : ""))
            .filter(Boolean);
          if (cartCourseIds.length > 0) {
            try {
              const draft = loadRegistrationFormDraft();
              saveRegistrationFormDraft({
                ...draft,
                cursos: mergeRegistrationCoursesWithCart(
                  draft.cursos,
                  cartCourseIds,
                  cartCourseIds,
                ),
              });
            } catch {
              /* cart draft sync must not block login */
            }
          }
        }

        persistLoginSession(data);
        try {
          await applyOwnedCoursesToStoredCart();
        } catch {
          /* ownership filter must not block login */
        }

        const nextPath = resolvePostLoginPath(data.rol, location.search);
        navigate(nextPath);
      } else {
        setError(
          typeof data?.error === "string" && data.error.trim()
            ? data.error
            : "No se pudo iniciar sesión. Verifica tus credenciales e inténtalo de nuevo."
        );
      }
    } catch (error) {
      setError("Error en el servidor. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <button className="btn-flotante-cursos" onClick={() => navigate("/")}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
        Ver Cursos
      </button>

      <div className="login-wrapper">
        <div className="login-header">
          <img src="/logos/LOGO_ACADEMIA.png" alt="Logo de la Academia" className="logo-academia ui-integrated-image" draggable="false" onContextMenu={(e) => e.preventDefault()} />
        </div>

        <div className="login-container">
          <h2>Iniciar Sesión</h2>
          <p className="login-subtitle">Accede a tu cuenta para continuar</p>

          <form onSubmit={handleLogin} className="login-form">
            {notice ? (
              <div className="mensaje-aviso" role="status">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {notice}
              </div>
            ) : null}
            {error ? (
              <div className="mensaje-error" role="alert">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                {error}
              </div>
            ) : null}

            <div className="input-group">
              <label htmlFor="login-email">Correo Electrónico</label>
              <input
                id="login-email"
                type="email"
                placeholder="ejemplo@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            </div>

            <div className="input-group">
              <label htmlFor="login-password">Contraseña</label>
              <div className="password-input-wrapper">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? getMessage("passwordVisibility.hide") : getMessage("passwordVisibility.show")}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            <button className="boton-inicio" type="submit" disabled={submitting}>
              {submitting ? "Entrando..." : "Iniciar Sesión"}
            </button>
          </form>

          <div className="links">
            <a href="/recuperar-password">¿Olvidaste tu contraseña?</a>
            <div className="links-divider"></div>
            <p>¿No tienes cuenta? <a href="/NewRegistro" className="link-highlight">Regístrate</a></p>
          </div>
        </div>
      </div>

      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Aún no estás inscrito en ningún curso</h3>
            <p>Consulta con un administrador o visita la página de cursos.</p>
            <div className="modal-actions">
              <button onClick={() => navigate("/")} className="btn-primary">Ir a Cursos</button>
              <button onClick={() => setMostrarModal(false)} className="btn-secondary">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;